/**
 * Preset layer builders — the shared scaffolding every atmosphere preset composes
 * from, so the per-biome files carry only their *distinguishing* values (colours,
 * counts, timings) instead of copy-pasted layer/modulator boilerplate.
 *
 * These are pure, output-preserving constructors: each emits exactly the
 * `EffectLayer` the inline literal it replaces did (verified by
 * `presets.snapshot.test.ts`). No defaults that change values — callers pass every
 * knob explicitly; the helpers only fold the repeated `kind` / `depth` / `y` /
 * modulator-structure scaffolding.
 *
 * Adopt where the pattern fits; layers that don't match a helper (bare glowBlobs
 * with no modulator, single foreground parallax planes, `tint`-modulated blobs,
 * …) stay inline in their preset file.
 */
import type { EffectLayer, ColorGradeSpec, VignetteSpec } from './effectLayer.types';
import type { GlowBlobSpec } from './presets.config';
import type { Modulator, ModTarget, ModShape } from './modulator';
import { Z_LAYERS } from '../../game/rendering/Z_LAYERS';

/** A single modulator with `shape` + `target` folded in (the sine swell every
 *  breathing/gusting layer rides). `phase` omitted entirely when undefined so the
 *  emitted object matches presets that authored no phase. */
export function modulator(
  target: ModTarget,
  shape: ModShape,
  periodMs: number,
  min: number,
  max: number,
  phase?: number,
): Modulator {
  return phase === undefined
    ? { target, shape, periodMs, min, max }
    : { target, shape, periodMs, min, max, phase };
}

/** The breathing `opacity` modulator most glow beds ride (swell → recede). */
export const opacityBreathe = (periodMs: number, min: number, max: number, phase?: number): Modulator =>
  modulator('opacity', 'sine', periodMs, min, max, phase);

/** The gusting `density` modulator most weather streams ride (surge → thin). */
export const densityGust = (periodMs: number, min: number, max: number, phase?: number): Modulator =>
  modulator('density', 'sine', periodMs, min, max, phase);

type GlowBlobsLayer = Extract<EffectLayer, { kind: 'glowBlobs' }>;
type WeatherLayer = Extract<EffectLayer, { kind: 'weather' }>;
type ParallaxLayer = Extract<EffectLayer, { kind: 'parallax' }>;

/** `glowBlobs` + a breathing opacity modulator — the most-repeated layer in the
 *  kit (a biome's floor/sky glow that swells + recedes). `extra` carries the rare
 *  per-layer knobs (textureKey/blendMode/seedOffset/…) when a preset needs them. */
export function breathingGlowBlobs(
  blobs: GlowBlobSpec[],
  breathe: { periodMs: number; min: number; max: number; phase?: number },
  extra?: Omit<GlowBlobsLayer, 'kind' | 'blobs' | 'modulators'>,
): GlowBlobsLayer {
  const layer: GlowBlobsLayer = {
    kind: 'glowBlobs',
    blobs,
    modulators: [opacityBreathe(breathe.periodMs, breathe.min, breathe.max, breathe.phase)],
  };
  return extra ? { ...extra, ...layer } : layer;
}

/** `weather` + a gusting density modulator — the directional particle stream
 *  (snow/embers/rain/petals/…) that surges + thins on a slow cycle. Pass every
 *  weather knob in `spec` in the same field order as the inline literal it
 *  replaces (keeps the emitted object readable + snapshot-friendly). */
export function gustingWeather(
  spec: Omit<WeatherLayer, 'kind' | 'modulators'>,
  gust: { periodMs: number; min: number; max: number; phase?: number },
): WeatherLayer {
  return { kind: 'weather', ...spec, modulators: [densityGust(gust.periodMs, gust.min, gust.max, gust.phase)] };
}

/** A background parallax plane minus the auto-assigned `depth` + `y`. */
export type ParallaxPlane = Omit<ParallaxLayer, 'kind' | 'depth' | 'y'>;

/** Three background parallax planes (far → mid → near) with `depth` auto-assigned
 *  to `PARALLAX_FAR`/`MID`/`NEAR` and `y: 0` (the scene-horizon anchor every
 *  multi-plane backdrop in the kit uses). Spread the result into a preset's
 *  `layers`. Per-plane `sway` is honoured individually (some biomes sway mid too). */
export function multiPlaneParallax(
  planes: [ParallaxPlane, ParallaxPlane, ParallaxPlane],
): ParallaxLayer[] {
  const depths = [Z_LAYERS.PARALLAX_FAR, Z_LAYERS.PARALLAX_MID, Z_LAYERS.PARALLAX_NEAR] as const;
  return planes.map((plane, i) => ({ kind: 'parallax', ...plane, y: 0, depth: depths[i] }));
}

/** The closing pair most biomes end on: an optional `vignette` (edge darken +
 *  biome wash) followed by the final `colorGrade` (the cinematic colour curve).
 *  Spread the result into a preset's `layers`. Presets with no vignette pass only
 *  `grade`. */
export function biomeCloser(closer: {
  vignette?: Omit<VignetteSpec, 'kind'>;
  grade?: ColorGradeSpec;
}): EffectLayer[] {
  const out: EffectLayer[] = [];
  if (closer.vignette) out.push({ kind: 'vignette', ...closer.vignette });
  if (closer.grade) out.push({ kind: 'colorGrade', ...closer.grade });
  return out;
}
